const ENHANCE_DELAY = 100;
const MAP_SCROLL_DELAY = 300;
const MAP_SCROLL_OFFSET = '120px';
const STACKED_LAYOUT = '(max-width: 991px)';
const UNLISTED_STORE = Number.MAX_SAFE_INTEGER;

function normalizeName(value) {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
}

class StoreLocator3dids extends HTMLElement {
  /** @type {Element | null} */
  #root = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #enhanceTimer = 0;
  #order = [];
  #scrollTimer = 0;

  connectedCallback() {
    this.#root = this.closest('.shopify-section') ?? document.body;
    this.#order = (this.dataset.storeOrder ?? '').split('\n').map(normalizeName).filter(Boolean);

    this.#observer = new MutationObserver((mutations) => {
      if (mutations.some(({ target }) => !target.closest?.('.sl-map'))) this.#schedule();
    });
    this.#observer.observe(this.#root, { childList: true, subtree: true });
    this.#root.addEventListener('click', this.#onClick);

    this.#schedule();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#root?.removeEventListener('click', this.#onClick);
    clearTimeout(this.#enhanceTimer);
    clearTimeout(this.#scrollTimer);
  }

  #schedule() {
    clearTimeout(this.#enhanceTimer);
    this.#enhanceTimer = setTimeout(() => this.#enhance(), ENHANCE_DELAY);
  }

  #enhance() {
    if (!this.#root) return;

    const input = this.#root.querySelector('#sl-search-input');
    const placeholder = this.dataset.searchPlaceholder;
    if (input && placeholder && input.placeholder !== placeholder) input.placeholder = placeholder;

    const list = this.#root.querySelector('.sl-addresses');
    if (!list) return;

    const featured = this.#sortStores(list);
    this.#insertSidebarImage();
    this.#insertFeaturedInfo(featured);
    this.#insertRetailersTitle(featured);
    this.#insertOnlineRetailers(list);
  }

  #orderIndex(item) {
    const name = normalizeName(item.querySelector('.sl-store-name')?.textContent);
    const index = this.#order.indexOf(name);
    return index === -1 ? UNLISTED_STORE : index;
  }

  #sortStores(list) {
    const items = [...list.querySelectorAll('.sl-address-item')];
    const sorted = items.toSorted((a, b) => this.#orderIndex(a) - this.#orderIndex(b));

    if (items.some((item, index) => item !== sorted[index])) {
      for (const item of sorted) list.appendChild(item);
    }

    const [first] = sorted;
    return first && this.#orderIndex(first) === 0 ? first : null;
  }

  #template(slot) {
    return this.querySelector(`template[data-slot="${slot}"]`);
  }

  #insertSidebarImage() {
    const template = this.#template('image');
    const anchor = this.#root.querySelector('.sl-address-list');
    if (!template || !anchor?.parentNode || this.#root.querySelector('.sidebar-store-image')) return;

    anchor.parentNode.insertBefore(template.content.cloneNode(true), anchor);
  }

  #insertFeaturedInfo(featured) {
    const template = this.#template('featured');
    const details = featured?.querySelector('.sl-address-details');
    if (!template || !details || details.querySelector('.store-locator-3dids__featured')) return;

    details.appendChild(template.content.cloneNode(true));
  }

  #insertRetailersTitle(featured) {
    const text = this.dataset.retailersTitle;
    if (!featured || !text) return;

    let title = this.#root.querySelector('.world-wide-title');
    if (!title) {
      title = document.createElement('li');
      title.className = 'world-wide-title';
      title.textContent = text;
    }
    if (featured.nextSibling !== title) featured.after(title);
  }

  #insertOnlineRetailers(list) {
    const template = this.#template('online');
    if (!template) return;

    let online = this.#root.querySelector('.world-wide-online-retailers');
    if (!online) {
      online = document.createElement('li');
      online.className = 'world-wide-online-retailers';
      online.appendChild(template.content.cloneNode(true));
    }
    if (list.lastElementChild !== online) list.appendChild(online);
  }

  #onClick = (event) => {
    if (!event.target.closest('.sl-address-item') || !window.matchMedia(STACKED_LAYOUT).matches) return;

    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => {
      const map = this.#root?.querySelector('.sl-map');
      if (!map) return;

      map.style.scrollMarginTop = MAP_SCROLL_OFFSET;
      map.scrollIntoView({ behavior: 'smooth', block: 'start' });
      map.style.scrollMarginTop = '';
    }, MAP_SCROLL_DELAY);
  };
}

if (!customElements.get('store-locator-3dids')) {
  customElements.define('store-locator-3dids', StoreLocator3dids);
}
